import { useEffect, useMemo, useRef } from 'react'
import type { JsonObject } from '@liveblocks/client'
import {
  useMutation as useLiveblocksMutation,
  useStorage,
  type DeviceJson,
  type ConnectionJson,
  type ZoneJson,
} from '@/lib/liveblocks'
import {
  useLayoutStore,
  installRemoteDispatcher,
  type LayoutDispatcher,
} from '@/stores/layoutStore'
import type { Device, Connection, Zone } from '@/types/devices'

/**
 * Bridge between the Zustand `layoutStore` and a Liveblocks
 * room's Storage.
 *
 *  - **Storage → Zustand:** subscribes to every `LiveMap` and the
 *    meta `LiveObject`; on any change (local OR remote), applies
 *    the full snapshot to Zustand via `applyRemoteSnapshot`.
 *    React Flow and everything else in the editor already reads
 *    from Zustand selectors, so peers see each other's changes
 *    with zero additional re-wiring.
 *
 *  - **Zustand → Storage:** installs a `LayoutDispatcher` that
 *    intercepts every mutation action and routes the write into
 *    Liveblocks Storage instead of Zustand. The subscription
 *    above then mirrors the change back into Zustand, so local
 *    and remote peers take the same code path. Echo-free by
 *    construction: local mutations don't touch Zustand directly.
 *
 *  - **Seed-on-first-connect:** if the room's Storage is empty
 *    (first peer to arrive), populate it from the REST snapshot
 *    that `useLayout` just hydrated into Zustand. Subsequent
 *    peers inherit Storage as-is.
 *
 * Only mounts inside `<LayoutRoom>`, which is itself gated on
 * the user having `teamSharing` features. Offline users and
 * `/editor/local-dev` never reach this hook — their mutations
 * fall through to Zustand's legacy path.
 */
export function useMultiplayerLayoutSync(): void {
  // ── Storage reads ─────────────────────────────────────────
  //
  // In Liveblocks v3, the selector passed to `useStorage` receives
  // a `ToJson<Storage>` view — which collapses each `LiveMap<K, V>`
  // into a plain `Readonly<Record<K, V>>` object, NOT a
  // `ReadonlyMap`. Calling `.values()` on the return value throws
  // `e.values is not a function` at runtime because plain objects
  // don't have that method. (This bit us after a Liveblocks major
  // upgrade — the TS `as ReadonlyMap<...>` cast hid the mismatch.)
  //
  // We normalize to a real `Map` here via `useMemo` so downstream
  // code can use idiomatic `.size` / `.values()` without caring
  // about the underlying snapshot shape. Identity is stable across
  // renders — the Map is rebuilt only when Liveblocks produces a
  // new snapshot object.
  const devicesRecord = useStorage(root => root.devices) as Readonly<
    Record<string, JsonObject>
  > | null
  const connectionsRecord = useStorage(root => root.connections) as Readonly<
    Record<string, JsonObject>
  > | null
  const zonesRecord = useStorage(root => root.zones) as Readonly<
    Record<string, JsonObject>
  > | null
  const meta = useStorage(root => root.meta) as {
    readonly name: string
    readonly version: number
  } | null

  const devicesMap = useMemo(
    () => (devicesRecord ? new Map(Object.entries(devicesRecord)) : null),
    [devicesRecord],
  )
  const connectionsMap = useMemo(
    () =>
      connectionsRecord ? new Map(Object.entries(connectionsRecord)) : null,
    [connectionsRecord],
  )
  const zonesMap = useMemo(
    () => (zonesRecord ? new Map(Object.entries(zonesRecord)) : null),
    [zonesRecord],
  )

  const applyRemoteSnapshot = useLayoutStore(s => s.applyRemoteSnapshot)
  const localState = useLayoutStore.getState

  // ── Seed Storage on first connect ─────────────────────────
  //
  // If Storage is empty and we already have REST-hydrated data in
  // Zustand, write it into Storage so peers can join. Runs once
  // per mount; `hasSeeded` guards against re-running after our
  // own seed write echoes back through `useStorage`.
  const seedStorage = useLiveblocksMutation(
    (
      { storage },
      payload: {
        devices: Device[]
        connections: Connection[]
        zones: Zone[]
        name: string
        version: number
      },
    ) => {
      const devMap = storage.get('devices')
      for (const d of payload.devices) devMap.set(d.id, d as DeviceJson)
      const connMap = storage.get('connections')
      for (const c of payload.connections)
        connMap.set(c.id, c as ConnectionJson)
      const zoneMap = storage.get('zones')
      for (const z of payload.zones) zoneMap.set(z.id, z as ZoneJson)
      storage.get('meta').update({
        name: payload.name,
        version: payload.version,
      })
    },
    [],
  )

  const hasSeeded = useRef(false)
  useEffect(() => {
    if (hasSeeded.current) return
    // Wait until useStorage has produced its first snapshot —
    // `null` means Storage is still loading from the Liveblocks
    // server.
    if (!devicesMap || !connectionsMap || !zonesMap || !meta) return

    const hasAnyEntity =
      devicesMap.size > 0 || connectionsMap.size > 0 || zonesMap.size > 0
    if (hasAnyEntity) {
      // Storage already has content — another peer populated it
      // (or this is a reconnect). Skip seed.
      hasSeeded.current = true
      return
    }

    const zustand = localState()
    if (zustand.devices.length === 0 && zustand.zones.length === 0) {
      // Nothing to seed with yet — `useLayout` hasn't finished
      // hydrating from REST. Wait for the next effect run.
      return
    }

    seedStorage({
      devices: zustand.devices,
      connections: zustand.connections,
      zones: zustand.zones,
      name: zustand.layoutName,
      version: zustand.layoutVersion,
    })
    hasSeeded.current = true
  }, [devicesMap, connectionsMap, zonesMap, meta, seedStorage, localState])

  // ── Storage → Zustand mirror ──────────────────────────────
  //
  // Skip while Storage is still empty AND Zustand has REST
  // data — otherwise we'd clobber the REST-hydrated snapshot
  // on render 2 (between useLayout resolving and our seed
  // write propagating through `useStorage`). Once the seed is
  // applied, the next render fires this effect with populated
  // maps and mirrors normally.
  useEffect(() => {
    if (!devicesMap || !connectionsMap || !zonesMap || !meta) return
    const storageEmpty =
      devicesMap.size === 0 &&
      connectionsMap.size === 0 &&
      zonesMap.size === 0 &&
      meta.version === 0
    if (storageEmpty) {
      const zustand = localState()
      const zustandHasData =
        zustand.devices.length > 0 || zustand.zones.length > 0
      if (zustandHasData) return
    }
    applyRemoteSnapshot({
      devices: Array.from(devicesMap.values()) as unknown as Device[],
      connections: Array.from(
        connectionsMap.values(),
      ) as unknown as Connection[],
      zones: Array.from(zonesMap.values()) as unknown as Zone[],
      name: meta.name,
      version: meta.version,
    })
  }, [
    devicesMap,
    connectionsMap,
    zonesMap,
    meta,
    applyRemoteSnapshot,
    localState,
  ])

  // ── Dispatcher: Zustand actions → Storage ─────────────────
  //
  // Each mutation bumps `meta.version` so `useMultiplayerAutoSave`
  // can detect "something changed" via a single scalar rather
  // than diffing three maps. The version is monotonic within a
  // room's lifetime and gets written back to Zustand by the
  // Storage subscription above.
  const addDevice = useLiveblocksMutation(({ storage }, device: Device) => {
    const devMap = storage.get('devices')
    devMap.set(device.id, device as DeviceJson)
    // Maintain zone.deviceIds when a device is created already
    // inside a zone. Mirrors the Zustand action's side-effect.
    if (device.zoneId) {
      const zoneMap = storage.get('zones')
      const zone = zoneMap.get(device.zoneId) as Zone | undefined
      if (zone && !zone.deviceIds.includes(device.id)) {
        zoneMap.set(device.zoneId, {
          ...zone,
          deviceIds: [...zone.deviceIds, device.id],
        } as ZoneJson)
      }
    }
    const m = storage.get('meta')
    m.update({ version: m.get('version') + 1 })
  }, [])

  const updateDevice = useLiveblocksMutation(({ storage }, device: Device) => {
    const devMap = storage.get('devices')
    const prev = devMap.get(device.id) as Device | undefined
    devMap.set(device.id, device as DeviceJson)
    // If zoneId changed, keep both old and new zone.deviceIds
    // consistent. Same logic as the Zustand path.
    if (prev && prev.zoneId !== device.zoneId) {
      const zoneMap = storage.get('zones')
      if (prev.zoneId) {
        const oldZone = zoneMap.get(prev.zoneId) as Zone | undefined
        if (oldZone) {
          zoneMap.set(prev.zoneId, {
            ...oldZone,
            deviceIds: oldZone.deviceIds.filter(id => id !== device.id),
          } as ZoneJson)
        }
      }
      if (device.zoneId) {
        const newZone = zoneMap.get(device.zoneId) as Zone | undefined
        if (newZone && !newZone.deviceIds.includes(device.id)) {
          zoneMap.set(device.zoneId, {
            ...newZone,
            deviceIds: [...newZone.deviceIds, device.id],
          } as ZoneJson)
        }
      }
    }
    const m = storage.get('meta')
    m.update({ version: m.get('version') + 1 })
  }, [])

  const deleteDevice = useLiveblocksMutation(
    ({ storage }, deviceId: string) => {
      storage.get('devices').delete(deviceId)
      // Cascade: remove any connections referencing the device,
      // mirroring the Zustand action.
      const connMap = storage.get('connections')
      for (const [id, conn] of connMap) {
        const c = conn as unknown as Connection
        if (c.sourceDeviceId === deviceId || c.targetDeviceId === deviceId) {
          connMap.delete(id)
        }
      }
      const m = storage.get('meta')
      m.update({ version: m.get('version') + 1 })
    },
    [],
  )

  const updateDevicePosition = useLiveblocksMutation(
    (
      { storage },
      args: { deviceId: string; position: { x: number; y: number } },
    ) => {
      const devMap = storage.get('devices')
      const prev = devMap.get(args.deviceId) as Device | undefined
      if (!prev) return
      devMap.set(args.deviceId, {
        ...prev,
        position: args.position,
      } as DeviceJson)
      const m = storage.get('meta')
      m.update({ version: m.get('version') + 1 })
    },
    [],
  )

  const addConnection = useLiveblocksMutation(
    ({ storage }, connection: Connection) => {
      storage
        .get('connections')
        .set(connection.id, connection as ConnectionJson)
      const m = storage.get('meta')
      m.update({ version: m.get('version') + 1 })
    },
    [],
  )

  const removeConnection = useLiveblocksMutation(
    ({ storage }, connectionId: string) => {
      storage.get('connections').delete(connectionId)
      const m = storage.get('meta')
      m.update({ version: m.get('version') + 1 })
    },
    [],
  )

  const createZone = useLiveblocksMutation(({ storage }, zone: Zone) => {
    storage.get('zones').set(zone.id, zone as ZoneJson)
    const m = storage.get('meta')
    m.update({ version: m.get('version') + 1 })
  }, [])

  const updateZone = useLiveblocksMutation(({ storage }, zone: Zone) => {
    storage.get('zones').set(zone.id, zone as ZoneJson)
    const m = storage.get('meta')
    m.update({ version: m.get('version') + 1 })
  }, [])

  const deleteZone = useLiveblocksMutation(({ storage }, zoneId: string) => {
    storage.get('zones').delete(zoneId)
    // Unassign any devices that pointed at this zone so they
    // pop back onto the top-level canvas (same as Zustand path).
    const devMap = storage.get('devices')
    for (const [id, dev] of devMap) {
      const d = dev as unknown as Device
      if (d.zoneId === zoneId) {
        devMap.set(id, { ...d, zoneId: undefined } as DeviceJson)
      }
    }
    const m = storage.get('meta')
    m.update({ version: m.get('version') + 1 })
  }, [])

  // Stable dispatcher reference — Liveblocks `useMutation`
  // returns memoised callbacks (with `[]` deps), so we only
  // re-install when one genuinely changes identity.
  const dispatcher = useMemo<LayoutDispatcher>(
    () => ({
      addDevice,
      updateDevice,
      deleteDevice,
      updateDevicePosition: (deviceId, position) =>
        updateDevicePosition({ deviceId, position }),
      addConnection,
      removeConnection,
      createZone,
      updateZone,
      deleteZone,
    }),
    [
      addDevice,
      updateDevice,
      deleteDevice,
      updateDevicePosition,
      addConnection,
      removeConnection,
      createZone,
      updateZone,
      deleteZone,
    ],
  )

  useEffect(() => {
    installRemoteDispatcher(dispatcher)
    return () => {
      installRemoteDispatcher(null)
    }
  }, [dispatcher])
}
